import type { Session } from "./api/types.ts";
import { useMixpanelClient } from "@workleap/telemetry/react";
import { type ReactNode, useEffect, useSyncExternalStore } from "react";
import { getUtmProperties } from "./tracking/getUtmProperties.ts";
import { useUserIdentification } from "./useUserIdentification.ts";

let sessionState: Session | null = null;
const listeners = new Set<() => void>();

export const sessionStore = {
    getSnapshot: (): Session | null => sessionState,
    setSession: (next: Session | null) => {
        if (sessionState === next) {
            return;
        }
        sessionState = next;
        listeners.forEach(listener => listener());
    },
    subscribe: (listener: () => void) => {
        listeners.add(listener);

        return () => {
            listeners.delete(listener);
        };
    }
};

export const useSession = () => {
    const session = useSyncExternalStore(sessionStore.subscribe, sessionStore.getSnapshot);

    if (!session) {
        throw new Error("useSession must be used within a SessionProvider");
    }

    return session;
};

interface SessionProviderProps {
    children: ReactNode;
    value: Session | null;
}

export function SessionProvider({ children, value: session }: SessionProviderProps) {
    const { identifyShareGateUser } = useUserIdentification();
    const mixpanelClient = useMixpanelClient();

    if (sessionStore.getSnapshot() !== session) {
        sessionStore.setSession(session);
    }

    useEffect(() => {
        if (session) {
            identifyShareGateUser(session.accountId);
        }
    }, [session, identifyShareGateUser]);

    useEffect(() => {
        const utmProperties = getUtmProperties();

        if (Object.keys(utmProperties).length > 0) {
            mixpanelClient.setGlobalEventProperties(utmProperties);
        }
    }, [mixpanelClient]);

    return children;
}
